import React from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import {imageUrl} from '../../../../assets/scripts/utils/shopify-images';
import IconPlus from '../../../../assets/svg/icon-plus.svg';

/* eslint-disable camelcase */
const CartUpsells = ({total, products, addToCart, freeShippingBarSettings}) => {
  const {serviceOne, serviceTwo, thresholdOne, thresholdTwo} = freeShippingBarSettings;

  /**
   * Determine which shipping service to display based on cart total
   * @returns {string|null} - The next achievable shipping service or null if all unlocked
   */
  const getTargetShippingService = () => {
    if (total < thresholdOne) {
      return serviceOne;
    } else if (thresholdTwo && total < thresholdTwo) {
      return serviceTwo;
    }
    return null;
  };

  const addUpsellProductToCart = async (variantId) => {
    if (!variantId) {
      return;
    }

    const item = {
      id: variantId,
      quantity: 1,
      properties: {
        _cart_upsell: true,
      },
    };
    await addToCart(item, false);
  };

  const targetService = getTargetShippingService();

  return (
    <>
      {total > 0 && (
        <div className="cart-upsells">
          <div className="cart-upsells__container">
            <div className="cart-upsells__header-wrapper">
              <p className="cart-upsells__header">
                {window.theme.strings.cartUpsells.title}
                {targetService && ` → Free ${targetService}`}
              </p>
              <div className="cart-upsells__header-divider" />
            </div>

            {products.map((product) => {
              const {variants, title, id, handle} = product;
              const firstAvailableVariant =
                variants && variants.length > 0
                  ? variants.find((variant) => variant.available === true) || variants[0]
                  : null;

              const price = firstAvailableVariant ? parseFloat(firstAvailableVariant.price) * 100 : 0;
              const compare_at_price =
                firstAvailableVariant && firstAvailableVariant.compare_at_price
                  ? parseFloat(firstAvailableVariant.compare_at_price) * 100
                  : null;
              return (
                <div key={id} className="cart-upsells__item cart-item">
                  <div className="cart-upsells__content-wrapper cart-item__content-wrapper">
                    <div className="cart-item__content">
                      <p className="cart-upsells__title cart-item__title">{title}</p>

                      {compare_at_price > price ? (
                        <div className="cart-upsells__price-container cart-item__price-container">
                          <div className="cart-upsells__price cart-item__compare">
                            <span className="cart-upsells__price--sale cart-item__price">
                              {formatMoney(price, window.theme.moneyFormat)}
                            </span>
                            <s className="cart-item__compare-price" data-compare-price>
                              {formatMoney(compare_at_price, window.theme.moneyFormat)}
                            </s>
                          </div>
                        </div>
                      ) : (
                        <div className="cart-upsells__price-container cart-item__price-container">
                          <div className="cart-upsells__price cart-item__price">
                            {formatMoney(price, window.theme.moneyFormat)}
                          </div>
                        </div>
                      )}

                      {!variants || variants.length === 1 ? (
                        <button
                          type="button"
                          className="cart-upsells__button btn btn--small btn--white"
                          onClick={() => addUpsellProductToCart(firstAvailableVariant?.id || variants[0]?.id)}
                        >
                          {window.theme.strings.cartUpsells.add}
                          <span dangerouslySetInnerHTML={{__html: IconPlus}} />
                        </button>
                      ) : (
                        <a href={`/products/${handle}`} className="cart-upsells__button btn btn--small">
                          {window.theme.strings.cartUpsells.view}
                        </a>
                      )}
                    </div>
                  </div>

                  {product.images && product.images.length > 0 && (
                    <div className="cart-upsells__image cart-item__image">
                      <a href={`/products/${handle}`} className="aspect-ratio aspect-ratio--portrait">
                        <img
                          src={imageUrl(product.images[0].src, 180, 240, 'center')}
                          width={90}
                          height={120}
                          alt={title}
                          loading="lazy"
                        />
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
/* eslint-enable camelcase */

CartUpsells.propTypes = {
  total: PropTypes.number,
  products: PropTypes.arrayOf(PropTypes.object),
  addToCart: PropTypes.func,
  freeShippingBarSettings: PropTypes.shape({
    serviceOne: PropTypes.string.isRequired,
    serviceTwo: PropTypes.string,
    thresholdOne: PropTypes.number.isRequired,
    thresholdTwo: PropTypes.number,
  }),
};

export default CartUpsells;
